<template>
  <section
    class="app-tabs relative max-w-full overflow-hidden text-neutral text-sm font-sans font-medium justify-start"
    :class="[tabClasses[variant].container]"
  >
    <ul
      class="tabs absolute left-0 flex select-none [justify-content:inherit] overflow-x-auto w-full"
      :class="[tabClasses[variant].wrapper]"
    >
      <li
        v-for="(tab, index) in tabs"
        :key="tab?.label + (index === selected ? 's' : '')"
        role="button"
        :class="[
          tabClasses[variant].tab,
          {
            [tabClasses[variant].activeTab]: index === selected
          }
        ]"
        class="cursor-pointer outline-primary-light outline-offset-[-1px] text-center text-neutral transition-colors duration-200"
        tabindex="0"
        @click="() => emit('update:selected', index)"
        @keydown.enter.space.stop="() => emit('update:selected', index)"
      >
        <Icon
          v-if="tab?.icon"
          class="w-full h-6 sm:h-8"
          :path="tab.icon"
          :class="[index === selected ? 'text-current' : 'text-[inherit]']"
        />
        <EditableElement
          v-if="editing === index"
          :id="`editable-tab-name-${index}`"
          class="ellipsis outline-primary-dark px-1 -mx-1"
          :model-value="tab.label"
          element="div"
          @update:model-value="
            label => {
              emit('rename', index, label);
              editing = -1;
            }
          "
        />
        <div
          v-else
          class="ellipsis"
          :class="{ 'opacity-60': !tab?.label }"
          @click.prevent="
            tab.editable && index === selected && renameTab(index)
          "
          @keydown.r="tab.editable && index === selected && renameTab(index)"
        >
          {{ tab?.label || defaultLabel }}
        </div>
        <IconButton
          v-if="tab.editable && index === selected"
          class="min-w-4 h-4 -ml-[0.33em] -mr-[0.5em] outline-offset-2"
          :path="mdiPencil"
          @click.stop.prevent="
            tab.editable && index === selected && renameTab(index)
          "
        />
        <IconButton
          v-if="closable"
          class="min-w-4 h-4 mr-[-2px] outline-offset-2"
          :path="mdiClose"
          @click.stop.prevent="() => emit('close', index)"
        />
      </li>
      <li
        v-if="addable"
        role="button"
        class="h-full cursor-pointer text-primary outline-primary-light outline-offset-[-1px]"
        tabindex="0"
        @click="() => emit('add')"
        @keydown.enter.space.prevent="() => emit('add')"
      >
        <Icon class="w-12 h-12 p-3" :path="mdiPlus" />
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { mdiClose, mdiPencil, mdiPlus } from '@mdi/js';
import type { PropType } from 'vue';

const EditableElement = defineAsyncComponent(
  () => import('@/async-components/EditableElement.vue')
);
const IconButton = defineAsyncComponent(
  () => import('@/async-components/Icon/Button.vue')
);

interface Tab {
  label?: string;
  icon?: string;
  editable?: boolean;
}

const defaultLabel = 'New tab';

const props = defineProps({
  tabs: {
    type: Array as PropType<Tab[]>,
    default: () => []
  },
  selected: {
    type: Number,
    default: -1
  },
  addable: {
    type: Boolean,
    default: false
  },
  closable: {
    type: Boolean,
    default: false
  },
  renamable: {
    type: Boolean,
    default: false
  },
  variant: {
    type: String as PropType<'default' | 'projects'>,
    default: 'default'
  }
});

type Emits = {
  (e: 'update:selected', index: number): void;
  (e: 'rename', index: number, value: string): void;
  (e: 'close', index: number): void;
  (e: 'add'): void;
};

const emit = defineEmits<Emits>();

const editing = ref(-1);

const tabClasses = computed(() => ({
  default: {
    container: 'min-h-[48px] w-full',
    wrapper: 'min-h-[48px] w-full',
    tab:
      'min-h-[48px] max-w-[22rem] py-1 px-1.5 sm:px-2 md:px-4 ' +
      'flex flex-wrap gap-0.5 items-center justify-center ' +
      'border-transparent border-y-[6px]',
    activeTab: '!border-b-blue'
  },
  projects: {
    container: 'min-h-[78px]',
    wrapper: 'min-h-[78px] md:rounded overflow-hidden w-full bg-neutral',
    tab:
      'min-h-[78px] min-w-[100px] lg:min-w-[120px] pb-1 md:pt-3 px-1 lg:px-1.5 sm:px-2 md:px-4 ' +
      'flex flex-col gap-0.5 items-center justify-center first:ml-auto last:mr-auto ' +
      'font-bold text-white bg-neutral',
    activeTab: 'bg-primary text-white'
  }
}));

async function renameTab(index: number) {
  if (props.renamable) {
    editing.value = index;
    await nextTick();
    const input = document.getElementById(`editable-tab-name-${index}`);
    if (input) {
      input.focus();
      if (input && window?.getSelection) {
        const range = document.createRange();
        range.selectNodeContents(input);
        window.getSelection()?.removeAllRanges();
        window.getSelection()?.addRange(range);
      }
    }
  }
}
</script>
